import { IDataFrameView, IDataFrameSchema } from '@/lib/dataFrameTypes';
import { RenderHeaderCellProps, RenderCellProps, textEditor } from 'react-data-grid';
import { booleanEditor, numericInput } from './Editors';
import 'react-data-grid/lib/styles.css';

import { IDataFrameField } from '@/lib/dataFrameTypes';

import DataCell from './DataCell';
import { ColumnWithDType } from '../types';
import HeaderCell, { getHeaderCellStatusClass } from './HeaderCell';
import intersection from 'lodash-es/intersection';

import {
  EDITABLE_FIELD_NAME_BLACKLIST,
  EDITABLE_RENDER_METHOD_WHITELIST,
  GRID_DISPLAY_FIELD_NAME_BLACKLIST,
  SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST,
  DIFF_STATUS_FIELD_NAME,
  DERRIVED_INDEX_FIELD_NAME,
} from '../consts';

const isSortable = (renderMethod: TRenderAsEnum) => {
  switch (renderMethod) {
    case 'struct':
    case 'list':
    case 'image':
    case 'diff_status':
      return false;
    default:
      return true;
  }
};

type TDataTypeRenderMethods = 'int' | 'float' | 'bool' | 'str' | 'list' | 'struct' | 'image' | 'link' | 'null' | '?';
type TCustomRenderMethods = 'image' | 'link' | 'code' | 'markdown';
type TStatusRenderMethods = 'diff_status';
export type TRenderAsEnum = TDataTypeRenderMethods | TCustomRenderMethods | TStatusRenderMethods | 'default';

const getDataTypeRenderMethod = (field: IDataFrameField): TDataTypeRenderMethods => {
  const { dtype } = field;
  const isInt = ['uint8', 'u16', 'u32', 'u64', 'i8', 'i16', 'i32', 'i64'].includes(dtype);
  const isFloat = ['f32', 'f64'].includes(dtype);
  const isList = dtype.startsWith('list[');
  const isStruct = dtype.startsWith('struct[');

  return isInt ? 'int' : isFloat ? 'float' : isList ? 'list' : isStruct ? 'struct' : (dtype as TDataTypeRenderMethods);
};

const getStatusRenderMethod = (field: IDataFrameField | ColumnWithDType): TStatusRenderMethods | undefined => {
  const isDiffStatusIndicator = field.name === DIFF_STATUS_FIELD_NAME;
  return isDiffStatusIndicator ? 'diff_status' : undefined;
};

const getCustomRenderMethod = (field: IDataFrameField | ColumnWithDType): TCustomRenderMethods | undefined => {
  const renderFunction = field?.metadata?._oxen?.render;
  const isImage = renderFunction && renderFunction.func === 'image';
  const isLink = renderFunction && renderFunction.func === 'link';
  const isCode = renderFunction && renderFunction.func === 'code';
  const isMarkdown = renderFunction && renderFunction.func === 'markdown';

  if (isCode) {
    return 'code';
  }
  if (isMarkdown) {
    return 'markdown';
  }
  if (isImage) {
    return 'image';
  }
  if (isLink) {
    return 'link';
  }
  return undefined;
};

export const getColumnRenderMethod = (field: IDataFrameField | ColumnWithDType): TRenderAsEnum => {
  return (
    getStatusRenderMethod(field as IDataFrameField) ||
    getCustomRenderMethod(field as IDataFrameField) ||
    getDataTypeRenderMethod(field as IDataFrameField)
  );
};

type TGetColumnsProps = {
  data: IDataFrameView;
  addedColumnNames?: string[];
  removedColumnNames?: string[];
  modifiedColumnNames?: string[];
  fileRootPath?: string;
  editable?: boolean;
};

const getEditor = (column: IDataFrameField | ColumnWithDType) => {
  const renderMethod = getColumnRenderMethod(column);
  if (renderMethod === 'int' || renderMethod === 'float') {
    return numericInput;
  }
  if (renderMethod === 'bool') {
    return booleanEditor;
  }
  return textEditor;
};

const isEditable = (
  field: IDataFrameField | ColumnWithDType,
  isDataframeEditable: boolean | undefined,
): boolean | undefined => {
  const renderMethod = getColumnRenderMethod(field);
  const fieldEditable =
    EDITABLE_RENDER_METHOD_WHITELIST.includes(renderMethod) &&
    !EDITABLE_FIELD_NAME_BLACKLIST.includes(field.name as string);

  if (!isDataframeEditable) {
    return undefined;
  }
  return fieldEditable && isDataframeEditable;
};

const buildColumn = (
  field: IDataFrameField,
  addedColumnNames: string[],
  removedColumnNames: string[],
  modifiedColumnNames: string[],
  fileRootPath: string,
  editable: boolean | undefined,
) => {
  const renderMethod = getColumnRenderMethod(field);
  const isAdded = !!addedColumnNames?.includes(field.name) || field.changes?.status === 'added';
  const isRemoved = !!removedColumnNames?.includes(field.name) || field.changes?.status === 'deleted';
  const isModified = !!modifiedColumnNames?.includes(field.name) || field.changes?.status === 'modified';
  const columnWidth = renderMethod === 'diff_status' ? 36 : undefined;

  return {
    key: field.name,
    name: field.name,
    renderCell: DataCell,
    metadata: field.metadata,
    fileRootPath: fileRootPath,
    dtype: field.dtype,
    added: isAdded,
    removed: isRemoved,
    width: columnWidth,
    maxWidth: columnWidth,
    minWidth: columnWidth,
    editable: isEditable(field, editable),
    renderEditCell: isEditable(field, editable) && getEditor(field),
    renderHeaderCell: HeaderCell,
    sortable: isSortable(renderMethod),
    headerCellClass: getHeaderCellStatusClass(isAdded, isRemoved, isModified),
  } as ColumnWithDType;
};

const getFilteredSortedFields = (fields: any[]) => {
  const filteredFields = fields.filter((field: any) => !GRID_DISPLAY_FIELD_NAME_BLACKLIST.includes(field.name));

  const statusField = fields.find((field: any) => field.name === DIFF_STATUS_FIELD_NAME);
  const indexField = fields.find((field: any) => field.name === DERRIVED_INDEX_FIELD_NAME);

  const sortedFields = filteredFields.filter(
    (field: any) => field.name !== DIFF_STATUS_FIELD_NAME && field.name !== DERRIVED_INDEX_FIELD_NAME,
  );

  if (indexField) sortedFields.unshift(indexField);
  if (statusField) sortedFields.unshift(statusField);
  return sortedFields;
};

const getColumns = ({
  data,
  addedColumnNames,
  removedColumnNames,
  modifiedColumnNames,
  fileRootPath,
  editable,
}: TGetColumnsProps): ColumnWithDType[] => {
  const schema = data?.schema;

  const sortedFields = getFilteredSortedFields(schema.fields);

  // console.log('sortedFields', sortedFields);

  return sortedFields.map((field: any) =>
    buildColumn(
      field,
      addedColumnNames || [],
      removedColumnNames || [],
      modifiedColumnNames || [],
      fileRootPath as string,
      editable,
    ),
  ) as ColumnWithDType[];
};

type TGetDataStructureProps = {
  dataFrame: IDataFrameView;
  compareResponse?: any;
  fileRootPath?: string;
  editable?: boolean;
};

export const hasEditableData = (viewSchema: IDataFrameSchema, sourceSchema: IDataFrameSchema) => {
  const sourceSchemaFieldsNames = sourceSchema.fields.map((field: any) => field.name);
  const viewSchemaFieldsNames = viewSchema.fields
    .map((field: any) => field.name)
    .filter((name) => !SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST.includes(name));

  const viewIsSubsetOfSource = viewSchemaFieldsNames.every((item) => sourceSchemaFieldsNames.includes(item));
  const schemaIncludesPrimaryKey = viewSchema.fields.some((field: any) => field.name === DERRIVED_INDEX_FIELD_NAME);
  // console.log('viewSchemaFieldsNames', viewSchemaFieldsNames);
  // console.log('sourceSchemaFieldsNames', sourceSchemaFieldsNames);
  // console.log('fieldIntersection', fieldIntersection);
  // console.log('schemaIncludesPrimaryKey', schemaIncludesPrimaryKey);

  return schemaIncludesPrimaryKey && viewIsSubsetOfSource;
};

export const getDataStructure = ({ dataFrame, compareResponse, fileRootPath, editable }: TGetDataStructureProps) => {
  const schemaDiff = compareResponse?.schema_diff;
  const addedColumnNames = schemaDiff?.added_cols?.map((field: any) => field.key) || [];
  const removedColumnNames = schemaDiff?.removed_cols?.map((field: any) => field.key) || [];
  const modifiedColumnNames = [] as string[];

  // console.log('viewSchema', dataFrame.schema);
  // console.log('sourceSchema', sourceSchema);

  // const sourceSchemaFieldsNames = sourceSchema.fields.map((field: any) => field.name);
  // const viewSchemaFieldsNames = dataFrame.schema.fields
  //   .map((field: any) => field.name)
  //   .filter((name) => !SCHEMA_DISPLAY_FIELD_NAME_BLACKLIST.includes(name));

  // const viewIsSubsetOfSource = viewSchemaFieldsNames.every((item) => sourceSchemaFieldsNames.includes(item));
  // console.log('viewIsSubsetOfSource', viewIsSubsetOfSource);

  // const fieldIntersection = intersection(viewSchemaFieldsNames, sourceSchemaFieldsNames);
  // console.log('fieldIntersection', fieldIntersection);
  // const schemaIncludesPrimaryKey = fieldIntersection.some((field: any) => field.name === DERRIVED_INDEX_FIELD_NAME);
  // console.log('schemaIncludesPrimaryKey', schemaIncludesPrimaryKey);

  // const includesPrimaryKey = schemaDiff?.added_cols?.some((field: any) => field.key === 'primary_key');

  const columns = getColumns({
    data: dataFrame,
    addedColumnNames,
    removedColumnNames,
    modifiedColumnNames,
    fileRootPath,
    editable,
  });

  const rows = dataFrame.data;
  return { columns, rows };
};

const getCellValue = (props: RenderCellProps<any>) => {
  const cell = props.row[props.column.name as any];
  const valueDefined = cell && typeof cell === 'object' && 'match' in cell;
  const value = valueDefined ? cell.value : props.row[props.column.name as any];
  return value;
};

export const getHeaderCellMetadata = (props: RenderHeaderCellProps<any>) => {
  const column = props.column as ColumnWithDType;
  const columnName = column.name as string;

  const isLeftCell = columnName.endsWith('.left');
  const isRightCell = columnName.endsWith('.right');
  const isCompareColumn = isLeftCell || isRightCell;

  const renderAs = getColumnRenderMethod(column as IDataFrameField);

  return {
    isCompare: isCompareColumn,
    renderAs,
  };
};

export const getDataCellMetadata = (props: RenderCellProps<any>) => {
  const column = props.column as ColumnWithDType;
  // const field = column as IDataFrameField;
  // console.log('getDataCellMetadata > field', field);
  const row = props.row;

  const columnName = column.name as string;

  const baseColumnName = ['left', 'right'].includes(columnName.split('.')[1]) ? columnName.split('.')[0] : columnName;
  const leftValue = row[`${baseColumnName}.left`]?.value;
  const rightValue = row[`${baseColumnName}.right`]?.value;
  const isAddedByColumn = row[`${baseColumnName}.right`] && !row[`${baseColumnName}.left`];
  const isRemovedByColumn = row[`${baseColumnName}.left`] && !row[`${baseColumnName}.right`];

  const isLeftCell = columnName.endsWith('.left');
  const isRightCell = columnName.endsWith('.right');

  // derrived from above
  const isCompareColumn = row[`${baseColumnName}.left`] || row[`${baseColumnName}.right`];
  const isChanged = isCompareColumn && leftValue !== rightValue; //|| column.modified;
  const isAdded = isCompareColumn && isRightCell && (isAddedByColumn || isChanged); //|| column.added;
  const isRemoved = isCompareColumn && isLeftCell && (isRemovedByColumn || isChanged); //|| column.removed;

  const renderAs = getColumnRenderMethod(column as IDataFrameField);

  return {
    isCompare: isCompareColumn,
    isChanged,
    isAdded,
    isRemoved,
    renderAs,
    value: getCellValue(props),
  };
};
